import React, {useEffect, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import {Spinner} from 'react-bootstrap';
import {ContainerNuevo, ContainerOptions, Explanation, InputError, InputOption, InputSend, LabelCart, LabelName} from './cuenta.elements';
import {ContenedorGlobal} from '../../components/Theme/theme.elements';
import {useTenantContext} from '../../tenant/TenantContext';
import {activateAccount, fetchActivationStatus} from '../infrastructure/UserApi';
import {isFailure, isPending, isSuccess} from '../../common/domain/RemoteData.model';

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

/**
 * /activar?token=…  — el cliente elige su contraseña con el enlace del correo de bienvenida.
 * El enlace solo existe si la empresa autorizó la solicitud en el admin.
 */
export const Activate = () => {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { name, tel } = useTenantContext();
  const [status, setStatus] = useState({ state: 'pending' });
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setStatus({ state: 'success', value: { valid: false, reason: 'Falta el enlace de activación. Usa el enlace del correo que te hemos enviado.' } });
      return undefined;
    }
    fetchActivationStatus(token).then(remote => { if (!cancelled) setStatus(remote); });
    return () => { cancelled = true; };
  }, [token]);

  const schema = Yup.object({
    password: Yup.string()
                 .required('Este campo es obligatorio')
                 .matches(PASSWORD_RULE, 'Mínimo 8 caracteres, con mayúscula, minúscula y número'),
    confirm: Yup.string()
                .oneOf([Yup.ref('password')], 'Las contraseñas no coinciden')
                .required('Confirma la contraseña')
  });

  const submit = async ({ password }) => {
    setResult({ state: 'pending' });
    setResult(await activateAccount({ token, password }));
  };

  const info = isSuccess(status) ? status.value : null;

  return (
    <div className="pt-4">
      <ContenedorGlobal className="narrow">
        <ContainerNuevo className="col-md-12 bs coloured" background={'#F0F0F0'}>
          <div className="col-md-12">
            <LabelName>Activar mi cuenta</LabelName>
          </div>

          {isPending(status) && (
            <div className="d-flex justify-content-center"><Spinner /></div>
          )}

          {isFailure(status) && (
            <div className="errorMessage">
              <p>No se ha podido comprobar el enlace. Reintenta más tarde o llámanos al {tel}.</p>
            </div>
          )}

          {info && !info.valid && (
            <div className="errorMessage">
              <p>{info.reason}</p>
              <p><Link to="/credenciales">Solicitar credenciales nuevas</Link> · <Link to="/iniciar-sesion">Volver a inicio de sesión</Link></p>
            </div>
          )}

          {info && info.valid && result && isSuccess(result) && (
            <div className="successMessage text-success">
              <p>Tu cuenta está activada. Ya puedes entrar con tu código de cliente <strong>{info.numCliente}</strong> y la contraseña que acabas de elegir.</p>
              <p><Link to="/iniciar-sesion">Ir a inicio de sesión</Link></p>
            </div>
          )}

          {info && info.valid && !(result && isSuccess(result)) && (
            <Formik initialValues={{ password: '', confirm: '' }} validationSchema={schema} onSubmit={submit}>
              {({ handleChange, handleBlur, values, errors, touched }) => (
                <Form>
                  <p>
                    Hola{info.nombre ? ` ${info.nombre}` : ''}. Tu usuario en el portal de {name} es el código de cliente
                    <strong> {info.numCliente}</strong>. Elige la contraseña con la que entrarás a partir de ahora.
                  </p>
                  <div className="row">
                    <ContainerOptions className="col-md-6">
                      <LabelCart className="mandatory">Nueva contraseña</LabelCart>
                      <InputOption type="password" id="password" autoComplete="new-password" value={values.password} onChange={handleChange} onBlur={handleBlur} />
                      <Explanation>Mínimo 8 caracteres, con mayúscula, minúscula y número</Explanation>
                      {errors.password && touched.password && (<InputError>{errors.password}</InputError>)}
                    </ContainerOptions>
                    <ContainerOptions className="col-md-6">
                      <LabelCart className="mandatory">Confirmar contraseña</LabelCart>
                      <InputOption type="password" id="confirm" autoComplete="new-password" value={values.confirm} onChange={handleChange} onBlur={handleBlur} />
                      {errors.confirm && touched.confirm && (<InputError>{errors.confirm}</InputError>)}
                    </ContainerOptions>
                  </div>
                  {result && isFailure(result) && (
                    <div className="errorMessage"><p>{result.error?.message ?? result.error ?? 'No se ha podido activar la cuenta.'}</p></div>
                  )}
                  <div className="d-flex flex-column flex-md-row justify-content-center justify-content-md-between align-items-md-end gap-2 submit-row">
                    <InputSend type="submit" value="Activar cuenta" disabled={result && isPending(result)} />
                    <div className="order-md-first text-center">
                      <Link to="/iniciar-sesion">Volver a inicio de sesión</Link>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </ContainerNuevo>
      </ContenedorGlobal>
    </div>
  );
};
